<template>
  <div class="group-container" :style="cssVars">
    <v-row class="ma-0 group-items" :class="[wrapItems, overflow]" no-gutters>
      <v-col
        v-for="(item, index) in items"
        :key="index"
        class="my-1"
        style="margin-left: 1px;"
        :class="
          isLastOnRow(index)
            ? 'item-col--no-right-space'
            : 'item-col--right-space'
        "
        cols="auto"
      >
        <v-card :width="itemWidth" height="100%" flat color="transparent" style="border-radius: 0px">
          <slot name="item" :item="item" :index="index">
            <ImageCard
              :imgSrc="getItemImgSrc(item)"
              :text="getItemText(item)"
              :lg="lg"
              @click="onItemClick(item)"
            />
          </slot>
        </v-card>
      </v-col>
    </v-row>

    <template v-if="showNavBtns">
      <v-btn
        absolute
        left
        top
        color="white"
        class="nav-btn"
        @click="moveBackwards"
      >
        <v-icon color="primary">chevron_left</v-icon>
      </v-btn>

      <v-btn
        absolute
        right
        top
        color="white"
        class="nav-btn"
        @click="moveForwards"
      >
        <v-icon color="primary">chevron_right</v-icon>
      </v-btn>
    </template>
  </div>
</template>

<script>
import { isTouchScreen } from '@/shared/utility';
import ImageCard from '@/components/generalComponents/uiComponents/ImageCard';

export default {
  name: 'ItemGroup',
  props: {
    items: Array,
    itemImgSrc: String,
    itemText: String,
    /**
     * Wrap items instead of overflowing to the right
     */
    wrap: Boolean,
    showArrows: {
      type: Boolean,
      default: true,
    },
    lg: Boolean,
    itemsPerRow: Number,
  },
  emits: ['select'],
  components: {
    ImageCard,
  },
  computed: {
    wrapItems() {
      // TODO: mobile breakpoint can be dynamic using props in the future
      if (this.$vuetify.breakpoint.smAndDown) {
        return 'flex-nowrap';
      }
      if (this.wrap) {
        return 'flex-wrap';
      }
      return 'flex-nowrap';
    },
    overflow() {
      if (this.$vuetify.breakpoint.smAndDown) {
        return 'overflow--auto';
      }
      if (this.wrap) {
        return 'overflow--none';
      }
      if (!this.isOverflown) {
        return 'overflow--none';
      }
      if (this.showNavBtns) {
        return 'overflow--none';
      }
      return 'overflow--auto';
    },
    wrapEnabled() {
      return this.wrapItems === 'flex-wrap';
    },
    computedItemsPerRow() {
      if (this.itemsPerRow > 0) {
        return this.itemsPerRow;
      }

      // name of breakpoint is not responsive, use width instead
      if (this.$vuetify.breakpoint.width < 601) {
        // xs
        if (this.lg) {
          return 1.1;
        }
        return 1.3;
      } else if (this.$vuetify.breakpoint.width < 961) {
        // sm
        if (this.lg) {
          return 2.1;
        }
        return 2.5;
      } else if (this.$vuetify.breakpoint.width < 1265) {
        // md
        if (this.lg) {
          return 2.5;
        }
        return 3;
      } else if (this.$vuetify.breakpoint.width < 1905) {
        // lg
        if (this.lg) {
          return 3;
        }
        return 4;
      } else {
        // xl
        if (this.lg) {
          return 4;
        }
        return 5;
      }
    },
    itemColWidth() {
      if (!this.isMounted || !this.computedItemsPerRow) {
        return 0;
      }

      const groupItems = this.$el.querySelector('.group-items');
      return groupItems.clientWidth / this.computedItemsPerRow;
    },
    itemWidth() {
      if (!this.isMounted || !this.itemColWidth) {
        return 0;
      }

      return this.itemColWidth - this.paddingRight;
    },
    moveSize() {
      const itemsPerRowInt = Math.floor(this.computedItemsPerRow);
      return itemsPerRowInt * (this.itemWidth + this.paddingRight);
    },
    isOverflown() {
      if (!this.isMounted) {
        return false;
      }

      if (this.items == null) {
        return false;
      }

      const groupItems = this.$el.querySelector('.group-items');

      let nrOfItems = 0;
      if (this.wrap) {
        nrOfItems = this.computedItemsPerRow;
      } else {
        nrOfItems = this.items.length;
      }

      const itemsTotalWidth =
        (nrOfItems - 1) * (this.itemWidth + this.paddingRight) + this.itemWidth;

      return groupItems.clientWidth < itemsTotalWidth;
    },
    showNavBtns() {
      if (isTouchScreen()) {
        return false;
      }
      return this.isOverflown;
    },
    scrollSteps() {
      if (this.scrollInterval == 0) {
        return 1;
      }
      return this.scrollDuration / this.scrollInterval;
    },
    cssVars() {
      return {
        '--space-right': `${this.paddingRight}px`,
      };
    },
  },
  data() {
    return {
      isMounted: false,
      paddingRight: 8, // 'px'
      scrollId: null,
      scrollDuration: 300, // milliseconds
      scrollInterval: 10, // milliseconds
    };
  },
  mounted() {
    this.isMounted = true;
  },
  beforeDestroy() {
    this.stopScroll();
  },
  methods: {
    getItemImgSrc(item) {
      if (this.itemImgSrc) {
        return item[this.itemImgSrc];
      }
      return ''; // Better return a default image
    },
    getItemText(item) {
      if (this.itemText) {
        return item[this.itemText];
      }
      return '';
    },
    moveBackwards() {
      this.smoothScroll(-1 * this.moveSize);
    },
    moveForwards() {
      this.smoothScroll(this.moveSize);
    },
    smoothScroll(scrollSize) {
      const groupItems = this.$el.querySelector('.group-items');

      if (this.scrollId || groupItems == null || !this.scrollSteps) {
        return;
      }

      let scrollStepSize = Math.round(scrollSize / this.scrollSteps);
      let sizeScrolled = 0;

      this.scrollId = setInterval(() => {
        if (Math.abs(scrollSize - sizeScrolled) < Math.abs(scrollStepSize)) {
          // Size left for scrolling is less than on step size, scroll by left size
          scrollStepSize = scrollSize - sizeScrolled;
        }

        groupItems.scrollBy(scrollStepSize, 0);
        sizeScrolled += scrollStepSize;

        if (Math.abs(sizeScrolled) >= Math.abs(scrollSize)) {
          this.stopScroll();
        }
      }, this.scrollInterval);
    },
    stopScroll() {
      clearInterval(this.scrollId);
      this.scrollId = null;
    },
    isLastOnRow(index) {
      if (!this.wrapEnabled) {
        // Horizontal layout
        return index === this.items.length - 1;
      }

      return (index + 1) % this.computedItemsPerRow === 0;
    },
    onItemClick(item) {
      this.$emit('select', item);
    },
  },
};
</script>

<style lang="scss" scoped>
.group-container {
  width: 100%;
  position: relative;

  &:hover {
    .nav-btn {
      width: 45px;
      height: 45px;
      opacity: 1;
    }
  }
}
.overflow--none {
  overflow: hidden hidden;
}
.overflow--auto {
  overflow: auto hidden;
}
.nav-btn {
  top: 50%;
  transform: translateY(-50%);
  opacity: 0;
  width: 0px;
  height: 0px;
  min-width: 0px !important;
  border-radius: 50%;
  transition: opacity 0.5s;
}
.item-col--right-space {
  margin-right: var(--space-right);
}
.item-col--no-right-space {
  margin-right: 0px;
}
</style>
